<script setup>
import { defineProps, defineEmits, ref, watch, onMounted, computed } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
const { TextInput, PrimaryButton } = useLayoutComponents();

const props = defineProps({
    show: {
        type: Boolean,
        default: false
    },
    fileInfo: {
        type: Object,
        required: true
    },
    mode: {
        type: String,
        default: 'preview',
        validator: (value) => ['preview', 'edit', 'listing-select'].includes(value)
    },
    isListing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close', 'save', 'update:modelValue', 'columns-selected']);

const descriptions = ref({});
const selectedColumns = ref(new Set());
const markedForRemoval = ref(new Set());
const originalDescriptions = ref({});
const originalSelectedColumns = ref(new Set());

const columns = computed(() => {


    if (!props.fileInfo?.schema) {
        console.warn('No schema found in file info');
        return [];
    }

    const processedColumns = props.fileInfo.schema.map(col => ({
        name: col.name || col.column_name || '',
        type: col.type || col.data_type || 'string',
        description: col.description || ''
    }));


    return processedColumns;
});

const initializeDescriptions = () => {

    if (props.fileInfo?.columnDescriptions) {
        descriptions.value = { ...props.fileInfo.columnDescriptions };
        originalDescriptions.value = { ...props.fileInfo.columnDescriptions };
    } else if (props.fileInfo?.schema) {
        const newDescriptions = {};
        if (props.fileInfo.schema.length > 0) {
            props.fileInfo.schema.forEach(col => {
                if (col.description) {
                    newDescriptions[col.name] = col.description;
                }
            });
        }
        descriptions.value = newDescriptions;
        originalDescriptions.value = { ...newDescriptions };
    } else {
        descriptions.value = {};
        originalDescriptions.value = {};
    }

    markedForRemoval.value.clear();
};

watch(() => props.fileInfo, initializeDescriptions, { immediate: true });
onMounted(initializeDescriptions);

const initializeSelectedColumns = () => {

    if (props.mode === 'listing-select') {
        if (props.fileInfo.selectedColumns) {
            selectedColumns.value = new Set(props.fileInfo.selectedColumns);
            originalSelectedColumns.value = new Set(props.fileInfo.selectedColumns);
        } else {
            const columnNames = columns.value.map(col => col.name);
            selectedColumns.value = new Set(columnNames);
            originalSelectedColumns.value = new Set(columnNames);
        }
    }
 
};

watch(() => props.fileInfo, initializeSelectedColumns, { immediate: true });
onMounted(initializeSelectedColumns);

const handleSave = () => {
 
    markedForRemoval.value.forEach(columnName => {
        if (descriptions.value[columnName]) {
            delete descriptions.value[columnName];
        }
    });
    markedForRemoval.value.clear();

    if (props.mode === 'listing-select') {
        const selectedColumnsArray = Array.from(selectedColumns.value);
        const updatedColumns = columns.value
            .filter(col => selectedColumns.value.has(col.name))
            .map(col => ({
                ...col,
                description: descriptions.value[col.name] || null
            }));

        const updatedFileInfo = {
            ...props.fileInfo,
            selectedColumns: selectedColumnsArray,
            columns: updatedColumns,
            schema: updatedColumns
        };

       
        emit('save', updatedFileInfo);
    } else {
        const updatedDescriptions = {};
        
        Object.keys(descriptions.value).forEach(key => {
            updatedDescriptions[key] = descriptions.value[key];
        });

        const updatedColumns = columns.value.map(col => ({
            ...col,
            description: descriptions.value[col.name] || null
        }));

        const updatedFileInfo = {
            ...props.fileInfo,
            columns: updatedColumns,
            columnDescriptions: updatedDescriptions,
            schema: updatedColumns
        };
      
        emit('columns-selected', Array.from(selectedColumns.value));
        emit('update:modelValue', props.fileInfo);
 
        emit('save', updatedFileInfo);
    }

    emit('close');
};

const handleClose = () => {
    descriptions.value = { ...originalDescriptions.value };
    selectedColumns.value = new Set(originalSelectedColumns.value);
    markedForRemoval.value.clear();
    
    emit('close');
};

const toggleAllColumns = () => {
    if (selectedColumns.value.size === columns.value.length) {
        selectedColumns.value.clear();
    } else {
        selectedColumns.value = new Set(columns.value.map(col => col.name));
    }
};

const removeDescription = (columnName) => {
    markedForRemoval.value.add(columnName);
};

const isMarkedForRemoval = (columnName) => {
    return markedForRemoval.value.has(columnName);
};

const undoRemove = (columnName) => {
    markedForRemoval.value.delete(columnName);
};

</script>

<template>
    <Teleport to="body">
        <div v-if="show" class="modal-overlay" @click.stop>
            <div class="modal-container" @click.stop>
                <header class="modal-header">
                    <div class="file-info">
                        <div class="flex items-center gap-3">
                            <div class="file-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                    <path
                                        d="M2 4C2 2.89543 2.89543 2 4 2H14L20 8V20C20 21.1046 19.1046 22 18 22H4C2.89543 22 2 21.1046 2 20V4Z"
                                        fill="currentColor" fill-opacity="0.2" />
                                    <path d="M14 2L20 8H15C14.4477 8 14 7.55228 14 7V2Z" fill="currentColor" />
                                </svg>
                            </div>

                            <div class="flex-1 min-w-0">
                                <h2 class="file-name" :title="fileInfo.name">
                                    <span class="block truncate md:overflow-visible md:whitespace-normal text-sm">{{
                                        fileInfo.name }}</span>
                                </h2>
                                <div class="file-meta">
                                    <span class="meta-item" :title="fileInfo.size">
                                        <span class="block truncate md:overflow-visible md:whitespace-normal">{{
                                            fileInfo.size }}</span>
                                    </span>
                                    <span class="meta-divider">•</span>
                                    <span class="meta-item" :title="fileInfo.type || 'parquet'">
                                        <span class="block truncate md:overflow-visible md:whitespace-normal">{{
                                            fileInfo.type }}</span>
                                    </span>
                                    <span class="meta-divider">•</span>
                                    <span class="meta-item" :title="fileInfo.mode || 'parquet'">
                                        <span class="block truncate md:overflow-visible md:whitespace-normal"> {{ mode
                                            === 'edit' ? 'Edit Mode' : mode === 'listing-select' ? 'Select Columns' :
                                            'Preview Mode' }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button class="close-button" @click="handleClose">
                        <svg xmlns="http://www.w3.org/2000/svg" class="icon" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </header>
                <div class="modal-content">
                    <div class="table-wrapper">
                        <div class="hidden md:block">
                            <table class="data-table">
                                <thead>
                                    <tr>
                                        <th v-if="mode === 'listing-select'" class="w-1/12">
                                            <input type="checkbox" :checked="selectedColumns.size === columns.length"
                                                @change="toggleAllColumns" class="checkbox" />
                                        </th>
                                        <th class="w-1/4">Column Name</th>
                                        <th class="w-1/6">Type</th>
                                        <th class="w-7/12">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="col in columns" :key="col.name">
                                        <td v-if="mode === 'listing-select'">
                                            <input type="checkbox" :checked="selectedColumns.has(col.name)" @change="$event.target.checked
                                                ? selectedColumns.add(col.name)
                                                : selectedColumns.delete(col.name)" class="checkbox" />
                                        </td>
                                        <td>
                                            <div class="column-name" :title="col.name">
                                                <span class="block truncate md:overflow-visible md:whitespace-normal">
                                                    {{ col.name }}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="type-tag" :title="col.type">
                                                <span class="block truncate md:overflow-visible md:whitespace-normal">
                                                    {{ col.type }}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div class="description-cell">
                                                <template v-if="mode === 'edit'">
                                                    <div class="flex items-center gap-2 w-full">
                                                        <TextInput v-if="!isMarkedForRemoval(col.name)" 
                                                            v-model="descriptions[col.name]"
                                                            placeholder="Add column description..."
                                                            class="description-input flex-1" />
                                                        <div v-else class="flex-1 flex items-center">
                                                            <span class="text-gray-400 italic">Description will be removed upon save</span>
                                                            <button @click="undoRemove(col.name)" 
                                                                class="undo-btn ml-2" 
                                                                title="Undo remove">
                                                                <span class="text-sm text-indigo-500 dark:text-indigo-400 underline">Undo</span>
                                                            </button>
                                                        </div>
                                                        <button v-if="descriptions[col.name] && !isMarkedForRemoval(col.name)" 
                                                            @click="removeDescription(col.name)"
                                                            class="remove-btn" 
                                                            title="Remove description">
                                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <span class="description-text">
                                                        <span
                                                            class="block truncate md:overflow-visible md:whitespace-normal"
                                                            :title="descriptions[col.name] || 'No description available'">
                                                            {{ descriptions[col.name] || 'No description available' }}
                                                        </span>
                                                    </span>
                                                </template>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="mobile-cards md:hidden">
                            <div v-for="col in columns" :key="col.name" class="column-card">
                                <div class="card-header">
                                    <div class="card-title-group">
                                        <h3 class="column-card-name" :title="col.name">
                                            <span class="block truncate">{{ col.name }}</span>
                                        </h3>
                                        <span class="type-tag-mobile" :title="col.type">
                                            <span class="block truncate">{{ col.type }}</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="card-content">
                                    <template v-if="mode === 'edit'">
                                        <div class="flex items-center gap-2">
                                            <TextInput v-if="!isMarkedForRemoval(col.name)" 
                                                v-model="descriptions[col.name]"
                                                placeholder="Add column description..." class="description-input flex-1" />
                                            <div v-else class="flex-1 flex items-center">
                                                <span class="text-gray-400 italic">Description will be removed upon save</span>
                                                <button @click="undoRemove(col.name)" 
                                                    class="undo-btn ml-2" 
                                                    title="Undo remove">
                                                    <span class="text-sm text-indigo-500 dark:text-indigo-400">Undo</span>
                                                </button>
                                            </div>
                                            <button v-if="descriptions[col.name] && !isMarkedForRemoval(col.name)" 
                                                @click="removeDescription(col.name)"
                                                class="remove-btn-mobile" 
                                                title="Remove description">
                                                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <p class="description-text-mobile"
                                            :title="descriptions[col.name] || 'No description available'">
                                            {{ descriptions[col.name] || 'No description available' }}
                                        </p>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <footer class="modal-footer">
                    <template v-if="mode === 'listing-select'">
                        <span class="text-sm text-gray-500">
                            {{ selectedColumns.size }} columns selected
                        </span>
                        <div class="flex gap-3">
                            <button class="secondary-button" @click="handleClose">Cancel</button>
                            <PrimaryButton @click="handleSave">Confirm Selection</PrimaryButton>
                        </div>
                    </template>
                    <template v-else-if="mode === 'edit'">
                        <button class="secondary-button" @click="handleClose">Cancel</button>
                        <PrimaryButton @click="handleSave">Save Changes</PrimaryButton>
                    </template>
                    <template v-else>
                        <PrimaryButton @click="handleClose">Close</PrimaryButton>
                    </template>
                </footer>
            </div>
        </div>
    </Teleport>
</template>

<style scoped lang="postcss">
.modal-overlay {
    @apply fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm;
    animation: fadeIn 0.2s ease-out;
}

.modal-container {
    @apply overflow-hidden w-full m-6 max-w-5xl bg-echoes-background-gray border border-echoes-border-light mt-20 rounded-lg md:rounded-xl shadow-2xl flex flex-col max-h-[85vh] md:max-h-[80vh] relative;
}

.modal-header {
    @apply px-4 md:px-6 py-4 flex items-center justify-between gap-4 bg-echoes-background-gray sticky top-0 z-20 border-b border-echoes-border-light;
}

.file-info {
    @apply flex-1 flex items-center justify-between min-w-0;
}

.file-icon {
    @apply w-8 h-8 flex-shrink-0 text-echoes-primary;
}

.icon {
    @apply w-5 h-5;
}

.file-name {
    @apply text-sm md:text-base font-semibold text-white max-w-[2px] md:max-w-xl;
}

.file-meta {
    @apply flex items-center gap-2 text-xs text-white;
}

.close-button {
    @apply hidden sm:block p-2 rounded-lg text-white/80 hover:text-red-500/80 bg-transparent hover:bg-white/5 transition-colors;
}

.data-table {
    @apply w-full border-collapse text-sm;
}

.data-table thead {
    @apply bg-echoes-background-black sticky z-10;
}

.data-table th {
    @apply px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider;
}

.data-table td {
    @apply px-6 py-2.5 align-middle border-b border-echoes-border-light;
}

.type-tag {
    @apply px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 inline-flex items-center justify-center;
}

.mobile-cards {
    @apply space-y-3 p-4 text-sm;
}

.column-card {
    @apply bg-echoes-background-black rounded-lg border border-echoes-border-gray overflow-hidden transition-shadow hover:shadow-md dark:hover:shadow-gray-900/30;
}

.card-header {
    @apply p-4 bg-echoes-background-black;
}

.card-title-group {
    @apply space-y-2;
}

.column-card-name {
    @apply text-sm font-medium text-white;
}

.type-tag-mobile {
    @apply inline-flex px-2 py-0.5 text-xs font-medium rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300;
}

.card-content {
    @apply p-4 truncate;
}

.description-text-mobile {
    @apply text-sm text-gray-600 dark:text-gray-300;
}

.description-text {
    @apply text-sm text-gray-600 dark:text-gray-300;
}

.modal-footer {
    @apply px-4 md:px-6 py-3 flex items-center justify-end gap-3 border-t border-echoes-border-light bg-echoes-background-gray sticky bottom-0 z-20;
}

.secondary-button {
    @apply px-4 py-2 rounded-lg text-sm font-medium border border-echoes-border-light text-white/80 hover:bg-echoes-background-black/80 transition-colors;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 768px) {
    .description-input {
        @apply w-full;
    }

    .modal-content {
        @apply pb-16;
    }
}

:deep(.text-input__field) {
    @apply text-sm;
}

.modal-content {
    @apply overflow-auto flex-1;
}

.checkbox {
    @apply w-4 h-4 text-echoes-primary border-echoes-border-gray rounded focus:ring-echoes-primary;
}

.remove-btn {
    @apply p-1.5 rounded-md text-gray-400 hover:text-red-500 bg-transparent hover:bg-red-500/10 transition-colors;
}

.remove-btn-mobile {
    @apply p-1.5 rounded-md text-gray-400 hover:text-red-500 bg-transparent hover:bg-red-500/10 transition-colors;
}

.undo-btn {
    @apply text-echoes-primary hover:text-echoes-primary/80 transition-colors;
}
</style>